/**
 * Validates if a string contains only letters and numbers
 * @param value String to validate
 * @returns boolean indicating if the string is valid
 */
export const isAlphanumeric = (value: string): boolean => {
  const alphanumericRegex = /^[a-zA-Z0-9\s]+$/;
  return alphanumericRegex.test(value);
};

/**
 * Sanitizes input string removing potential XSS attacks
 * @param value String to sanitize
 * @returns Sanitized string
 */
export const sanitizeInput = (value: string): string => {
  if (!value) return '';
  
  // Remove HTML tags
  let sanitized = value.replace(/<[^>]*>/g, '');
  
  // Remove potential script injections
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  return sanitized;
};

/**
 * Validates string length
 * @param value String to validate
 * @param minLength Minimum length (default: 0)
 * @param maxLength Maximum length (default: 1000)
 * @returns Error message or null if valid
 */
export const validateLength = (
  value: string, 
  minLength: number = 0, 
  maxLength: number = 1000
): string | null => {
  const length = value.trim().length;
  
  if (length < minLength) {
    return `Il campo deve contenere almeno ${minLength} caratteri`;
  }
  
  if (length > maxLength) {
    return `Il campo non può superare ${maxLength} caratteri`;
  }
  
  return null;
};

/**
 * Validates email format
 * @param email Email to validate
 * @returns Error message or null if valid
 */
export const validateEmail = (email: string): string | null => {
  if (!email) return "Email richiesta";
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return "Formato email non valido";
  }
  
  if (email.length > 255) {
    return "Email troppo lunga";
  }
  
  return null;
};

/**
 * Validates URL format
 * @param url URL to validate
 * @returns Error message or null if valid
 */
export const validateUrl = (url: string): string | null => {
  if (!url) return null; // URL is optional in most cases
  
  try {
    new URL(url.startsWith('http') ? url : `https://${url}`);
    return null;
  } catch {
    return "URL non valido";
  }
};

/**
 * Validates numeric input
 * @param value String to validate
 * @param min Minimum value
 * @param max Maximum value
 * @returns Error message or null if valid
 */
export const validateNumber = (
  value: string, 
  min?: number, 
  max?: number
): string | null => {
  const num = parseFloat(value.replace(',', '.'));
  
  if (isNaN(num)) {
    return "Valore numerico non valido";
  }
  
  if (min !== undefined && num < min) {
    return `Il valore deve essere almeno ${min}`;
  }
  
  if (max !== undefined && num > max) {
    return `Il valore non può superare ${max}`;
  }
  
  return null;
};

/**
 * Returns an error message if the value contains special characters
 * @param value String to validate
 * @returns Error message or null if valid
 */
export const getSpecialCharactersError = (value: string): string | null => {
  if (!value) return null;
  
  // Sanitize first
  const sanitized = sanitizeInput(value);
  
  // Check length
  const lengthError = validateLength(sanitized, 0, 200);
  if (lengthError) return lengthError;
  
  // Check alphanumeric
  if (!isAlphanumeric(sanitized)) {
    return "Errore, non si possono utilizzare caratteri speciali come apostrofi, linee, punti ecc...";
  }
  
  return null;
};

/**
 * Validates if an EAN field is valid
 * @param value String to validate
 * @returns boolean indicating if the EAN is valid or empty (both are acceptable)
 */
export const isValidEan = (value: string): boolean => {
  if (!value) return true; // empty EAN is valid
  
  const sanitized = sanitizeInput(value);
  const lengthError = validateLength(sanitized, 0, 50);
  if (lengthError) return false;
  
  return isAlphanumeric(sanitized);
};

